import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { overrideDeferralSchema } from '@/entities/donor/model/override-deferral.schema'
import type { OverrideDeferralFormValues } from '@/entities/donor/model/override-deferral.schema'
import { useOverrideDeferralMutation } from '@/entities/donor/api/override-deferral.api'
import { toaster } from '@/shared/ui'
import { getAxiosErrorData } from '@/shared/utils'
import type { IApiError, IApiErrorField } from '@/shared/types/api'

/**
 * Either the override endpoint or the files upload it runs first can fail here,
 * and the files service leaves `errors`/`fields` null instead of empty.
 */
interface IErrorBody {
  errors:
    | (Omit<IApiError, 'fields'> & { fields: IApiErrorField[] | null })[]
    | null
}

interface Options {
  donorId: string
  defaultDeferralId?: string
  onSuccess?: () => void
}

/** Backend validation errors come back keyed by payload field, not form field. */
const API_FIELD_TO_FORM_FIELD: Partial<
  Record<string, keyof OverrideDeferralFormValues>
> = {
  deferral_id: 'deferralId',
  reason_text: 'reasonText',
  certificate_scan_url: 'certificateFile',
}

export function useOverrideDeferralForm({
  donorId,
  defaultDeferralId,
  onSuccess,
}: Options) {
  const { t } = useTranslation()
  const schema = useMemo(() => overrideDeferralSchema(t), [t])
  const { mutateAsync, isPending } = useOverrideDeferralMutation()

  const form = useForm<OverrideDeferralFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      deferralId: defaultDeferralId ?? '',
      reasonText: '',
    },
  })

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      await mutateAsync({
        donorId,
        deferralId: data.deferralId,
        reasonText: data.reasonText,
        certificateFile: data.certificateFile,
      })
      toaster.success({
        title: t('dashboard.override_deferral.success'),
      })
      form.reset()
      onSuccess?.()
    } catch (error) {
      const body = getAxiosErrorData<IErrorBody>(error)
      if (body?.errors?.[0]?.message) {
        body.errors.forEach((apiError) => {
          // 200000 is the backend's field-validation code — surface those
          // inline on the offending input instead of as a toast. The files
          // service answers with `fields: null`, so don't assume an array.
          if (apiError.code === '200000' && apiError.fields?.length) {
            apiError.fields.forEach((field: IApiErrorField) => {
              const formField = API_FIELD_TO_FORM_FIELD[field.key]
              if (formField) {
                form.setError(formField, { message: field.value })
              } else {
                toaster.error({ title: field.value })
              }
            })
          } else {
            toaster.error({ title: apiError.message })
          }
        })
        return
      }
      toaster.error({
        title: t('dashboard.override_deferral.error'),
      })
    }
  })

  return { form, isPending, onSubmit }
}
